import {
  Component, ElementRef, HostListener, Input, OnInit, ViewChild, AfterViewInit
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AlertService, AlertType } from '../../services/alert.service';
import { OrchestratorSearchService } from '../../services/API/orchestrator-search.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { SearchBarResponse } from '../../services/responses/orchestrator-search.response';
import { SearchBarStruct } from '../../services/structs/orchestrator-search/orchestrator-search.struct';
import { MatIcon } from '@angular/material/icon';
import { AiAgentService } from '../../services/API/ai-agent.service';

type ChatRole = 'user' | 'assistant' | 'system';
interface SearchItem { nome: string; email: string; url: string; }

type Intencao = 'Criar' | 'Pesquisar' | 'Navegar' | 'NaoReconhecido' | null;
type Entidade = 'Cliente' | 'Fornecedor' | 'Usuario' | 'Produto' | 'Geral' | 'NaoReconhecido' | null;

interface CardLink {
  label: string;
  url: string;
}

interface CardItem {
  title: string;
  subtitle?: string;
  snippet?: string | null;
  entidade?: string | null;
  score?: number;
  links: CardLink[];
}

interface ChatMessage {
  role: ChatRole;
  content?: string;   // texto/markdown
  cards?: CardItem[]; // cards
}

@Component({
    selector: 'app-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.css'],
    imports: [
        CommonModule, FormsModule, ReactiveFormsModule, RouterModule, MatAutocompleteModule
    ]
})
export class SearchBarComponent implements OnInit, AfterViewInit {
  @Input() isCompact = false;
  @Input() placeholder = 'Faça sua busca';

  searchText = '';
  _listSearchBarStruct: SearchItem[] = [];

  isChatOpen = false;
  chatInput = '';
  messages: ChatMessage[] = [];
  loading = false;
  errorText = '';

  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('chatBody') chatBodyRef?: ElementRef<HTMLDivElement>;

  constructor(
    private router: Router,
    private orchestratorSearchService: OrchestratorSearchService,
    private alertService: AlertService,
    private aiAgentService: AiAgentService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.searchInputRef?.nativeElement?.focus?.();
  }

  onTextChange(value: string): void {
    if (!value) return;
    this.searchText = value;

    if (this.router.url === '/home' && value.trim()) {
      this.aiAgentService.abrir();
      this.aiAgentService.prefill$.next(value);
      this.searchText = '';
    }
  }

  selectValue(url: string): void {
    window.location.href = url;
  }

  /** ==== CHAT OVERLAY ==== */
  toggleChat(open: boolean): void {
    this.isChatOpen = open;
    this.errorText = '';
    if (open && this.messages.length === 0) {
      this.messages.push({
        role: 'assistant',
        content: 'Olá! Posso buscar rotas/telas e conteúdos do sistema. O que você procura?'
      });
      setTimeout(() => this.scrollChatToBottom(), 0);
    }
  }

  openChatWithPrompt(prompt?: string): void {
    this.toggleChat(true);
    if (prompt && prompt.trim()) {
      this.chatInput = prompt.trim();
      this.sendChat();
    }
  }

  /** Enter envia (Shift+Enter quebra linha) */
  onEnter(ev: any): void {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      this.sendChat();
    }
  }

  /** Atalhos globais: Ctrl/Cmd+K abre; Esc fecha */
  @HostListener('document:keydown', ['$event'])
  onGlobalKey(ev: KeyboardEvent): void {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'k') {
      ev.preventDefault();
      this.toggleChat(true);
    }
    if (ev.key === 'Escape' && this.isChatOpen) {
      ev.preventDefault();
      this.toggleChat(false);
    }
  }

  /** === Busca no backend e responde no chat, sempre renderizando CARDS === */
  async sendChat(): Promise<void> {
    const text = (this.chatInput || '').trim();
    if (!text || this.loading) return;

    this.errorText = '';
    this.messages.push({ role: 'user', content: this.escape(text) });
    this.chatInput = '';
    this.loading = true;
    this.scrollChatToBottom();

    const MAX_MENU_CARDS = 20;    // aumentado p/ mostrar mais itens
    const MAX_CONTENT_CARDS = 12;

    this.orchestratorSearchService.Search(text).subscribe({
      next: (response: SearchBarResponse) => {
        if (response.isError) {
          this.messages.push({
            role: 'assistant',
            content: this.renderMarkdown(`**Erro ao buscar:** ${response.errorDescription || 'Falha desconhecida.'}`),
          });
          return;
        }

        const parts: string[] = [];
        const cards: CardItem[] = [];
        const seenUrls = new Set<string>();

        // Texto do assistente (quando vier)
        if (response.assistantReply?.trim()) {
          parts.push(this.renderMarkdown(response.assistantReply));
        }

        // 1) Card de ação direta (SE existir directLink) — mas sem interromper: ainda listamos sugestões
        if (response.directLink) {
          const url = '/' + response.directLink.replace(/^\//, '');
          const action = this.guessAction(response.assistantReply, url);
          const target = this.guessTargetFromUrl(url);
          const place  = this.formatPlaceLabel(url);
          const directCard: CardItem = {
            title: `${action} ${target}`.trim(),
            subtitle: place || undefined,
            links: [{ label: `${action} ${target}`, url }]
          };
          cards.push(directCard);
          seenUrls.add(url.toLowerCase());
        }

        // 2) Cards vindos de listSearchBarStruct (menus/itens)
        const items = response.listSearchBarStruct || [];
        if (items.length) {
          const limit = response.directLink ? MAX_MENU_CARDS : MAX_MENU_CARDS; // mesmo limite
          for (const it of items.slice(0, limit)) {
            const title = it.nome || it.email || it.cpfCnpj || 'Resultado';
            const { subtitle, snippet } = this.buildSubtitleAndSnippet(it);
            const links: CardLink[] = [];

            (it.listUrl || []).forEach(u => {
              const href = '/' + u.replace(/^\//, '');
              const key = href.toLowerCase();
              if (seenUrls.has(key)) return; // evita duplicar directLink nas sugestões
              seenUrls.add(key);
              links.push({ label: this.formatPlaceLabel(href), url: href });
            });

            if (links.length) {
              cards.push({
                title,
                subtitle,
                snippet,
                entidade: it.entidade || null,
                score: it.score,
                links
              });
            }
          }
        }

        // 3) Se não houver listSearchBarStruct, usa citations como fallback
        if (!items.length && response.citations?.length) {
          for (const cit of response.citations.slice(0, MAX_CONTENT_CARDS)) {
            const href = '/' + (cit.url || '').replace(/^\//, '');
            const key  = href.toLowerCase();
            if (seenUrls.has(key)) continue;
            seenUrls.add(key);
            cards.push({
              title: cit.title || 'Resultado',
              score: cit.score,
              links: [{ label: this.formatLinkLabel(href), url: href }]
            });
          }
        }

        if (!parts.length && !cards.length) {
          parts.push(this.renderMarkdown('Não encontrei resultados para sua busca.'));
        }

        this.messages.push({
          role: 'assistant',
          content: parts.length ? parts.join('<br/>') : undefined,
          cards: cards.length ? cards : undefined,
        });
      },
      error: () => {
        this.messages.push({
          role: 'assistant',
          content: this.renderMarkdown('Falha ao consultar o backend. Tente novamente.')
        });
      },
      complete: () => {
        this.loading = false;
        this.scrollChatToBottom();
      },
    });
  }

  private ACTION_WORDS = new Set(['novo', 'editar', 'detalhe', 'detalhes', 'visualizar', 'incluir', 'adicionar', 'criar']);

  private splitPath(url: string): string[] {
    return url.replace(/^\//, '').split('/').filter(Boolean);
  }

  private filterPlaceSegments(url: string): string[] {
    return this.splitPath(url).filter(seg => {
      if (this.ACTION_WORDS.has(seg.toLowerCase())) return false;
      if (/^\d+$/.test(seg)) return false; // id numérico
      return true;
    });
  }

  /** "cadastro/clientes/novo" -> "Cadastro › Clientes" (retira ação e ids) */
  private formatPlaceLabel(url: string): string {
    const segs = this.filterPlaceSegments(url)
      .map(s => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
    return segs.join(' › ');
  }

  /** Alvo a partir da URL filtrada */
  private guessTargetFromUrl(url: string): string {
    const segs = this.filterPlaceSegments(url);
    let last = (segs[segs.length - 1] || 'registro').replace(/-/g, ' ');
    if (/\w+es$/i.test(last)) last = last.replace(/es$/i, 'e');
    else if (/\w+s$/i.test(last)) last = last.replace(/s$/i, '');
    return last.charAt(0).toUpperCase() + last.slice(1);
  }

  /** Ação a partir do texto e/ou URL */
  private guessAction(txt?: string, url?: string): 'Novo' | 'Editar' | 'Visualizar' | '' {
    const t = (txt || '').toLowerCase();
    const has = (w: string) => new RegExp(`\\b${w}\\b`).test(t);
    if (['criar', 'novo', 'incluir', 'adicionar'].some(has)) return 'Novo';
    if (['editar', 'alterar'].some(has)) return 'Editar';
    if (['visualizar', 'ver', 'detalhe', 'detalhes'].some(has)) return 'Visualizar';

    if (url) {
      const u = url.toLowerCase();
      if (/(^|\/)(novo|criar|incluir)(\/|$)/.test(u)) return 'Novo';
      if (/(^|\/)editar(\/|$)/.test(u)) return 'Editar';
      if (/(^|\/)(detalhe|detalhes)(\/|$)/.test(u)) return 'Visualizar';
    }
    return "";
  }

  private formatLinkLabel(href: string): string {
    const parts = href.replace(/^\//, '').split('/').filter(Boolean);
    if (!parts.length) return href;
    return parts.map(p => p.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(' › ');
  }

  private escapeInline(s: string): string {
    return (s || '').replace(/[\*\_\`\[\]\<\>]/g, ch => '\\' + ch);
  }

  private closeSearch(): void {}

  private scrollChatToBottom(): void {
    try {
      const el = this.chatBodyRef?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }

  openLink(url: string, newTab = false): void {
    if (!url) return;
    this.toggleChat(false);

    if (/^https?:\/\//i.test(url)) {
      if (newTab) window.open(url, '_blank');
      else window.location.href = url;
      return;
    }

    const cleaned = '/' + url.replace(/^\//, '');
    if (newTab) window.open(cleaned, '_blank');
    else this.router.navigateByUrl(cleaned);
  }

  private buildSubtitleAndSnippet(it: SearchBarStruct): { subtitle: string; snippet: string | null } {
    const uniq: string[] = [];
    const add = (v?: string | null) => { if (v && !uniq.includes(v)) uniq.push(v); };

    add(it.email);
    add(it.telefone);
    add(it.cpfCnpj);

    const subtitle = uniq.join(' • ');

    let snip = (it.snippet || '').trim();
    if (!snip) return { subtitle, snippet: null };

    let parts = snip.split(/\n+/)
      .flatMap(s => s.split(/(?<=[.!?])\s+(?=[A-Z(])/))
      .map(s => s.trim())
      .filter(Boolean);

    const telDigits = this.onlyDigits(it.telefone);
    const docDigits = this.onlyDigits(it.cpfCnpj);

    parts = parts.filter(p => {
      const norm = this.normalizeStr(p);
      const repeatsText = uniq.some(u => norm.includes(this.normalizeStr(u)));
      const repeatsTel = telDigits && this.onlyDigits(p).includes(telDigits);
      const repeatsDoc = docDigits && this.onlyDigits(p).includes(docDigits);
      return !(repeatsText || repeatsTel || repeatsDoc);
    });

    const cleaned = parts.join(' ').replace(/\s{2,}/g, ' ').trim();
    return { subtitle, snippet: cleaned.length >= 8 ? cleaned : null };
  }

  private normalizeStr(s: string): string {
    return (s || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^\w@]+/g, ' ')
      .trim();
  }
  private onlyDigits(s?: string | null): string {
    return (s || '').replace(/\D/g, '');
  }

  private escape(s: string): string {
    return s.replace(/[&<>"']/g, (m) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    } as any)[m]);
  }

  private renderMarkdown(s: string): string {
    let out = s;
    out = out.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, t, u) => `<a href="${u}">${t}</a>`);
    out = out.replace(/\n/g, '<br/>');
    return out;
  }
}
